import { useRootLoader } from '~/utils/use-root-loader';
import { useState } from 'react';
import { DetailedProductFragment } from '~/generated/graphql';
import { normalizePrice } from '~/utils/normalize-price';
import { calculateEffectivePrice } from '~/utils/pricing';
import { Price } from '~/components/products/Price';
import { RegularOrderPanel } from './RegularOrderPanel';
import { AdvancedOrderPanel, DeliverySchedule } from './AdvancedOrderPanel';
import {
  MessageCircle,
  Minus,
  Plus,
  ShoppingBag,
  ShoppingCart,
  Truck,
  CheckCircle,
} from 'lucide-react';
import { FetcherWithComponents } from '@remix-run/react';
import { CartLoaderData } from '~/routes/api.active-order';
import { useGaEvents } from '~/utils/use-ga-events';

interface ProductOrderWidgetProps {
  product: DetailedProductFragment;
  selectedVariant: DetailedProductFragment['variants'][0];
  quantity: number;
  setQuantity: (val: number) => void;
  activeOrderFetcher: FetcherWithComponents<CartLoaderData>;
  isItemInCart: boolean;
  isSubmitting: boolean;
  // Variant Selection Props
  variants: DetailedProductFragment['variants'];
  onVariantChange: (variantId: string) => void;
}

export function ProductOrderWidget({
  product,
  selectedVariant,
  quantity,
  setQuantity,
  activeOrderFetcher,
  isItemInCart,
  isSubmitting,
  variants,
  onVariantChange,
}: ProductOrderWidgetProps) {
  const merchantContext = useRootLoader().merchantContext;
  const merchantContextRequired =
    merchantContext.capability.canSwitchMerchant &&
    !merchantContext.activeMerchant;
  const [activeTab, setActiveTab] = useState<'regular' | 'advanced'>('regular');
  const [advancedFrequency, setAdvancedFrequency] = useState<
    'daily' | 'weekly' | 'custom'
  >('daily');
  const [advancedOrderCount, setAdvancedOrderCount] = useState(0);
  const [deliverySchedule, setDeliverySchedule] =
    useState<DeliverySchedule | null>(null);
  const { trackAddToCart } = useGaEvents();

  // Always show quantity controls (quantity = items per order)
  const showQuantityControls = true;

  // Effective quantity for price calculation
  // Regular: just the quantity
  // Advanced: quantity per order × number of orders
  // OrderLine.quantity is quantity per delivery; occurrence expansion belongs
  // to schedule finalization on the backend.
  const deliveryCount = activeTab === 'advanced' ? advancedOrderCount : 1;
  const displayedQuantity = quantity * deliveryCount;

  // Safety check
  if (!selectedVariant) return null;

  // Price Calculation Logic
  const baseUnitPriceValue = (() => {
    if (typeof selectedVariant.priceWithTax === 'number') {
      return selectedVariant.priceWithTax;
    }
    const normalized = normalizePrice(selectedVariant.priceWithTax);
    return normalized?.__typename === 'SinglePrice' ? normalized.value : 0;
  })();

  const { effectiveUnitPrice, activeTier: activeBulkTier } =
    calculateEffectivePrice(
      baseUnitPriceValue,
      selectedVariant.customFields?.priceTiers,
      quantity,
    );

  const totalPriceValue = effectiveUnitPrice * displayedQuantity;
  const totalPrice = normalizePrice({ value: totalPriceValue });
  const currencyCode = selectedVariant.currencyCode;

  // Daily Installment Calculation (60-day repayment cycle)
  const dailyInstallmentValue = totalPriceValue / 60;
  const dailyInstallmentActual = dailyInstallmentValue / 100;
  const formattedDailyInstallment = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(dailyInstallmentActual);

  // Bulk Discount Calculation
  const customOriginalPrice = selectedVariant?.customFields?.originalPrice;
  const originalTotal =
    (customOriginalPrice || baseUnitPriceValue) * displayedQuantity;
  const savings = originalTotal - totalPriceValue;
  const savingsPercent =
    originalTotal > 0 ? Math.round((savings / originalTotal) * 100) : 0;

  const whatsappMessage = `Hi! I'm interested in ${product.name}${
    selectedVariant?.sku ? ` (Model: ${selectedVariant.sku})` : ''
  }. Can you provide more details?`;

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sticky top-24">
      {/* Tabs */}
      <div className="bg-[#FFF0F3] p-1 rounded-xl flex mb-6">
        <button
          onClick={() => setActiveTab('regular')}
          className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
            activeTab === 'regular'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Regular Order
        </button>
        <button
          onClick={() => setActiveTab('advanced')}
          className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
            activeTab === 'advanced'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Advanced Order
        </button>
      </div>

      {/* Variant Selector */}
      {variants.length > 1 && (
        <div className="mb-6">
          <label className="text-xs font-semibold text-gray-900 mb-2 block">
            Choose Variant
          </label>
          <div className="flex flex-wrap gap-2">
            {variants.map((variant) => (
              <button
                key={variant.id}
                onClick={() => onVariantChange(variant.id)}
                className={`px-4 py-2 text-xs font-medium rounded-lg border transition-all ${
                  selectedVariant.id === variant.id
                    ? 'border-[#E80245] bg-white text-[#E80245] ring-1 ring-[#E80245]'
                    : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {variant.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content Panels */}
      {activeTab === 'regular' ? (
        <RegularOrderPanel
          product={product}
          selectedVariant={selectedVariant}
          quantity={quantity}
          onQuantityChange={(q) => setQuantity(q)}
          currencyCode={currencyCode}
        />
      ) : (
        <AdvancedOrderPanel
          frequency={advancedFrequency}
          onFrequencyChange={setAdvancedFrequency}
          onOrderCountChange={setAdvancedOrderCount}
          onScheduleChange={setDeliverySchedule}
        />
      )}

      {/* Dynamic Summary Box */}
      <div className="mt-8 pt-6 border-t border-gray-100 space-y-3">
        {activeBulkTier && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Bulk Pricing Applied</span>
            <span className="text-[#E80245] font-medium">
              -
              <Price
                priceWithTax={{ value: savings, __typename: 'SinglePrice' }}
                currencyCode={currencyCode}
                variant="primary"
              />{' '}
              ({savingsPercent}%)
            </span>
          </div>
        )}

        <div className="flex items-start justify-between">
          <div className="flex flex-col">
            <span className="text-sm text-gray-500 font-medium">Total:</span>
            {displayedQuantity > 1 && (
              <div className="flex items-center gap-1 text-[11px] text-gray-400 font-medium mt-0.5">
                <span>{displayedQuantity}</span>
                <span>×</span>
                <Price
                  priceWithTax={{
                    value: effectiveUnitPrice,
                    __typename: 'SinglePrice',
                  }}
                  currencyCode={currencyCode}
                />
              </div>
            )}
          </div>
          <div className="flex flex-col items-end">
            {originalTotal > totalPriceValue && (
              <div className="relative inline-block">
                <span className="text-sm text-gray-400">
                  <Price
                    priceWithTax={{
                      value: originalTotal,
                      __typename: 'SinglePrice',
                    }}
                    currencyCode={currencyCode}
                  />
                </span>
                <div className="absolute top-1/2 left-0 w-full h-[1.5px] bg-[#E80245] -translate-y-1/2 transform rotate-[-3deg]"></div>
              </div>
            )}
            <span className="text-xl font-extrabold text-gray-900">
              <Price priceWithTax={totalPrice} currencyCode={currencyCode} />
            </span>
            <span className="text-xs text-gray-500 mt-1 font-normal">
              Or pay रू {formattedDailyInstallment}/day for 60 days
            </span>
          </div>
        </div>
      </div>

      {/* Integration with Remix Form for Add to Cart */}
      <activeOrderFetcher.Form
        method="post"
        action="/api/active-order"
        className="space-y-4"
        onSubmit={() => trackAddToCart(selectedVariant, quantity)}
      >
        <input type="hidden" name="action" value="addItemToOrder" />
        {/* Quantity synced via input hidden, but we also have UI controls below */}
        <input type="hidden" name="quantity" value={quantity} />
        <input type="hidden" name="variantId" value={selectedVariant.id} />
        {activeTab === 'advanced' && deliverySchedule && (
          <input
            type="hidden"
            name="customFields"
            value={JSON.stringify({
              itemType: deliverySchedule.isRepeatOrder ? 'recurring' : 'future',
              frequency: deliverySchedule.frequency,
              intervalStep: deliverySchedule.customInterval,
              startDate: deliverySchedule.isRepeatOrder
                ? new Date(deliverySchedule.startDate).toISOString()
                : new Date(
                    deliverySchedule.singleDeliveryDate || '',
                  ).toISOString(),
              endDate: deliverySchedule.isRepeatOrder
                ? new Date(deliverySchedule.endDate).toISOString()
                : new Date(
                    deliverySchedule.singleDeliveryDate || '',
                  ).toISOString(),
              totalDeliveries: deliverySchedule.orderCount,
              quantityPerDelivery: quantity,
              daysOfWeek: deliverySchedule.selectedDays,
            })}
          />
        )}

        <div className="flex gap-4">
          {/* Quantity Counter UI - Hidden for Advanced Daily/Weekly */}
          {showQuantityControls && (
            <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 h-12 px-2">
              <button
                type="button"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900"
              >
                <Minus size={16} />
              </button>
              <div className="w-8 text-center font-semibold text-gray-900">
                {quantity}
              </div>
              <button
                type="button"
                onClick={() => setQuantity(quantity + 1)}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900"
              >
                <Plus size={16} />
              </button>
            </div>
          )}

          {/* Main Add Button */}
          <button
            type="submit"
            className="flex-1 bg-[#E80245] hover:bg-[#D0023D] text-white font-bold rounded-xl h-12 flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={
              isSubmitting ||
              merchantContextRequired ||
              (activeTab === 'regular' &&
                selectedVariant.stockLevel === 'OUT_OF_STOCK') ||
              (activeTab === 'advanced' && advancedOrderCount === 0)
            }
          >
            {isSubmitting ? (
              <span className="animate-pulse">Processing...</span>
            ) : (
              <>Add to cart</>
            )}
          </button>
        </div>

        {/* Chat On Whatsapp Button */}
        <a
          href={`https://wa.me/9779863335289?text=${encodeURIComponent(
            whatsappMessage,
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-xl h-10 flex items-center justify-center gap-2 transition-colors text-sm"
        >
          <span>Chat on Whatsapp</span>
          <MessageCircle size={16} />
        </a>

        <div className="space-y-3 pt-2">
          <div className="flex items-start gap-3">
            <Truck className="w-5 h-5 text-[#E80245] flex-shrink-0 mt-0.5" />
            <span className="text-sm text-gray-600 leading-tight">
              Next Day Delivery available if ordered before 12 AM.
            </span>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-[#E80245] flex-shrink-0 mt-0.5" />
            <span className="text-sm text-gray-600 leading-tight">
              Quality guaranteed by Redtab. Easy returns within 24h.
            </span>
          </div>
        </div>
      </activeOrderFetcher.Form>
    </div>
  );
}
